import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { FormikRadioGroup } from './FormikRadioGroup';
import { Box, TextField } from '@material-ui/core';
import type { FormField } from '../interfaces';
import { findCamundaValidation, getLocalizedLabel } from '../utils';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';
import { useTextLengthFeedback } from '../hooks/useTextLengthFeedback';

export const GarantiMottattSelector = ({
    isRequired,
    isReadOnly,
    field,
    fields,
    locale,
}: {
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
    fields?: Array<FormField>;
    locale: string;
}) => {
    const classes = getFormFieldStyles();
    const { setFieldValue } = useFormikContext();
    const [garantiMottattFormikField] = useField(field?.id as string);
    const [garantiKommentarFormikField, garantiKommentarFormikMeta] =
        useField('garantiKommentar');
    const [showConditionalField, setShowConditionalField] = useState(undefined);
    const garantiKommentarLengthFeedback = useTextLengthFeedback(
        garantiKommentarFormikField.value,
        garantiKommentarFormikMeta,
        { showCounter: true, isReadOnly: Boolean(isReadOnly) },
    );

    const garantiKommentarField = fields?.find(
        (f) => f.id === garantiKommentarFormikField?.name,
    );

    const garantiKommentarIsRequired = findCamundaValidation(
        'required',
        garantiKommentarField?.validation,
    );

    useEffect(() => {
        if (
            garantiMottattFormikField?.value === 'true' ||
            showConditionalField === true
        ) {
            if (
                !garantiKommentarFormikField?.value &&
                garantiKommentarIsRequired
            ) {
                setFieldValue(garantiKommentarFormikField?.name, '');
            }
        }
    }, [garantiMottattFormikField?.value, showConditionalField]);

    return (
        <Box className={classes.container}>
            <Box>
                <FormikRadioGroup
                    {...garantiMottattFormikField}
                    id={field?.id}
                    label={getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field?.label,
                        field?.id,
                    )}
                    isRequired={isRequired}
                    isReadOnly={isReadOnly}
                    callback={setShowConditionalField}
                    locale={locale}
                />{' '}
            </Box>
            {showConditionalField === false ||
            garantiKommentarFormikField?.value ||
            garantiMottattFormikField?.value === 'false' ||
            garantiKommentarLengthFeedback.error ? (
                <Box paddingTop="15px">
                    <TextField
                        variant="outlined"
                        fullWidth={true}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        id={garantiKommentarField?.id}
                        {...garantiKommentarFormikField}
                        required={garantiKommentarIsRequired}
                        disabled={isReadOnly}
                        label={getLocalizedLabel(
                            `project.formFields.${garantiKommentarField?.id}`,
                            garantiKommentarField?.label,
                            garantiKommentarField?.id,
                        )}
                        multiline={true}
                        inputProps={{
                            'aria-readonly': !!isReadOnly,
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        defaultValue={
                            garantiKommentarField?.value ||
                            garantiKommentarField?.defaultValue
                        }
                        helperText={garantiKommentarLengthFeedback.helperText}
                        error={garantiKommentarLengthFeedback.error}
                    />
                </Box>
            ) : null}
        </Box>
    );
};
